import { splitTrim } from 'src/app/utils/functions/split-trim';

import {
  BbdParameter,
  ImageType,
  ImageUrlOptions,
  ImageUrlOptionsLights,
  ImageUrlOptionsRooftop
} from './image-url-options.interface';

/**
 * Creates a dynamic ImageUrl object from an url string, which can be modified
 * by passing image url options.
 */
export class ImageUrl {
  public static readonly defaultPerspective = 'BE020';

  // ImageType construction: A delivers JPG, P delivery PNG; 27 is predefined clipping
  public static readonly defaultImageType: ImageType = ImageType.A27;

  // fallback to be transparent to be safe for every vehicle Class (example: S-class does not support museum)
  public static readonly defaultBackground = '9';

  /**
   * the url object
   */
  public readonly url: URL;

  /**
   * the key string for CE images
   */
  public readonly ceKey: string;

  private options: ImageUrlOptions;

  public constructor(private readonly uri: string) {
    this.url = new URL(this.uri);
    this.options = this.getOptionsFromUrl(this.url);
    this.ceKey = (uri.match(/\?([^\&]+)/) || [null, null])[1];
    if (this.ceKey) {
      // avoid bbd issues by removing trailing equals signs from ceKey's base64 hash
      // and adding trailing ampersand to separate hash param from key/value params
      this.ceKey = `${this.ceKey.replace(/\=+$/g, '')}`;
    }
  }

  /**
   * Returns ImageUrlOptions extracted from given url
   */
  public getOptionsFromUrl(url: URL | string): ImageUrlOptions {
    url = typeof url === 'string' ? new URL(url) : url;
    const imgt: ImageType = (url.searchParams.get(BbdParameter.Imgt) as ImageType) || ImageUrl.defaultImageType;
    const background = url.searchParams.get(BbdParameter.Bkgnd) || ImageUrl.defaultBackground;
    const pov = splitTrim(url.searchParams.get(BbdParameter.Pov) || ImageUrl.defaultPerspective);
    const width = parseInt(url.searchParams.get('width') || '0', 10);
    const height = parseInt(url.searchParams.get('height') || '0', 10);
    const crop = splitTrim(url.searchParams.get('crop') || '').map(val => parseInt(val, 10));

    return this.castToImageUrlOptions(imgt, background, pov, width, height, crop);
  }

  /**
   * Set options to ImageUrl
   */
  public setOptions(options: ImageUrlOptions): void {
    this.options = this.mergeOptions(this.options, options);
    this.applyOptionsToUrl(this.url, this.options);
  }

  /**
   * get url as string without escaping characters
   * (because bbd service gives a shit about web standards)
   */
  public toString(): string {
    const { protocol, hostname, pathname } = this.url;

    const params = [];

    if (this.ceKey && this.ceKey.startsWith('q=')) {
      params.push(this.ceKey);
    } else if (this.ceKey) {
      params.push(`${this.ceKey}&`);
    }

    this.url.searchParams.forEach((value, key) => {
      if (value.length && key !== 'q') {
        params.push(`${key}=${Array.isArray(value) ? value.join(',') : value}`);
      }
    });

    const paramsJoined = params.length ? `?${params.join('&')}` : '';

    return `${protocol}//${hostname}${pathname}${paramsJoined}`;
  }

  /**
   * Returns Image URL options object
   */
  private castToImageUrlOptions(
    imgt: ImageType,
    background: string,
    pov: string[],
    width: number,
    height: number,
    crop: number[]
  ): ImageUrlOptions {
    const lights = pov.find(val =>
      (Object.values(ImageUrlOptionsLights) as string[]).includes(val)
    ) as ImageUrlOptionsLights;

    const rooftop = pov.find(val =>
      (Object.values(ImageUrlOptionsRooftop) as string[]).includes(val)
    ) as ImageUrlOptionsRooftop;

    return {
      perspective: pov[0],
      imageType: imgt,
      background,
      ...(!!crop.length ? { crop } : {}),
      ...(!!width ? { width } : {}),
      ...(!!height ? { height } : {}),
      ...(!!lights ? { lights } : {}),
      ...(!!rooftop ? { rooftop } : {})
    };
  }

  /**
   * Merge multiple ImageUrlOptions to one
   */
  private mergeOptions(...optionsStack: ImageUrlOptions[]): ImageUrlOptions {
    return optionsStack.reduce<ImageUrlOptions>(
      (merged, options) => ({
        imageType: options.imageType || merged.imageType || ImageUrl.defaultImageType,
        perspective: options.perspective || merged.perspective || ImageUrl.defaultPerspective,
        background: options.background || merged.background || ImageUrl.defaultBackground,
        ...['crop', 'width', 'height', 'lights', 'rooftop', 'isCeImage'].reduce(
          (opts, key) => ({
            ...opts,
            ...(options[key] || merged[key] ? { [key]: options[key] || merged[key] } : {})
          }),
          {}
        )
      }),
      {}
    );
  }

  /**
   * Apply ImageUrlOptions to an URL
   */
  private applyOptionsToUrl(url: URL | string, options: ImageUrlOptions): URL {
    url = typeof url === 'string' ? new URL(url) : url;

    if (options.isCeImage) {
      url.searchParams.set(BbdParameter.Imgt, options.imageType);
      url.searchParams.set(BbdParameter.Bkgnd, options.background);
      url.searchParams.set(
        BbdParameter.Pov,
        [
          options.perspective,
          ...(options.lights ? [options.lights] : []),
          ...(options.rooftop ? [options.rooftop] : [])
        ].join(',')
      );

      if (!!options.height) {
        url.searchParams.set(BbdParameter.Height, options.height.toString());
      }

      if (!!options.crop) {
        url.searchParams.set(BbdParameter.Crop, options.crop.join(','));
      }
    }

    if (!!options.width) {
      url.searchParams.set(BbdParameter.Width, options.width.toString());
    }

    return url;
  }
}
